import React, { useMemo } from 'react';
import { Spin, Typography } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PrintPreviewChrome } from '@/components/print/PrintPreviewChrome';
import { PrintableWorkOrder } from '@/components/manufacturing/PrintableWorkOrder';
import workOrderService from '@/services/workOrder.service';
import { useSystemSettings } from '@/hooks/useSettings';
import type { SystemSetting } from '@/services/settingsService';

const { Text } = Typography;

function getSetting(settings: SystemSetting[] | undefined, key: string): string {
  if (!settings?.length) return '';
  const s = settings.find((x) => x.setting_key === key);
  return (s?.setting_value ?? '').trim();
}

const WorkOrderPrintPage: React.FC = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const navigate = useNavigate();
  const { data: wo, isLoading, error } = useQuery({
    queryKey: ['work-orders', workOrderId],
    queryFn: () => workOrderService.getById(workOrderId!),
    enabled: Boolean(workOrderId),
  });
  const { data: settings } = useSystemSettings();

  const company = useMemo(() => {
    const list = settings?.filter((s) => s.category === 'company') ?? [];
    const legal = getSetting(list, 'company_legal_name') || getSetting(list, 'company_name') || 'Company';
    const logo = getSetting(list, 'company_logo') || undefined;
    return { legalName: legal, logoUrl: logo };
  }, [settings]);

  if (!workOrderId) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">Missing work order id</Text>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !wo) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">Work order not found.</Text>
      </div>
    );
  }

  return (
    <PrintPreviewChrome
      title={`Work order — ${wo.work_order_number}`}
      fileName={wo.work_order_number}
      onBack={() => navigate(`/mfg/work-orders/${workOrderId}`)}
    >
      <PrintableWorkOrder workOrder={wo} companyLegalName={company.legalName} companyLogoUrl={company.logoUrl} />
    </PrintPreviewChrome>
  );
};

export default WorkOrderPrintPage;
